import { useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useI18n } from '../../i18n';

interface RegisterFormProps {
  error: string | null;
  onRegister: (email: string, password: string, displayName: string, inviteCode: string) => Promise<void>;
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ error, onRegister, onSwitchToLogin }: RegisterFormProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || !displayName.trim() || !inviteCode.trim()) return;
    setBusy(true);
    try {
      await onRegister(email.trim(), password, displayName.trim(), inviteCode.trim());
    } finally {
      setBusy(false);
    }
  };

  const passwordValid = password.length >= 8;

  return (
    <form onSubmit={handleSubmit} className="lobby-form">
      <h2>{t('auth.registerTitle')}</h2>

      {error && <div className="lobby-error">{error}</div>}

      <label>
        {t('auth.inviteCode')}
        <Input
          data-testid="register-invite"
          value={inviteCode}
          onChange={e => setInviteCode(e.target.value)}
          maxLength={20}
          disabled={busy}
          required
          autoFocus
          placeholder={t('auth.inviteCodePlaceholder')}
        />
        <span className="text-xs text-muted-foreground">{t('auth.inviteCodeHint')}</span>
      </label>

      <label>
        {t('auth.email')}
        <Input
          type="email"
          data-testid="register-email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={busy}
          required
        />
      </label>

      <label>
        {t('auth.displayName')}
        <Input
          data-testid="register-displayname"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          maxLength={50}
          disabled={busy}
          required
        />
      </label>

      <label>
        {t('auth.passwordHint')}
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            data-testid="register-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={busy}
            required
            minLength={8}
            maxLength={128}
            className="pr-9"
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            disabled={busy}
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </label>

      <Button
        type="submit"
        disabled={busy || !inviteCode.trim() || !email.trim() || !passwordValid || !displayName.trim()}
      >
        {busy ? t('auth.registerBusy') : t('auth.registerSubmit')}
      </Button>

      <button
        type="button"
        onClick={onSwitchToLogin}
        disabled={busy}
        className="lobby-link-btn self-center"
      >
        {t('auth.hasAccount')}
      </button>
    </form>
  );
}
